import React, {
  ChangeEventHandler,
  FormEventHandler,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { email as checkEmail, customCondition, required } from '@hozana/form/validate'
import { useIsHydrated } from '@hozana/hooks/useIsHydrated'
import { useIsNative } from '@hozana/hooks/useIsNative'
import { useDispatch } from '@hozana/redux/hooks'
import { useRouter } from '@hozana/router'
import { bindRoutineToHookForm, routineToThunk } from '@hozana/routines/functions'
import { useScreen } from '@hozana/screen/useScreen'
import { QA } from '@hozana/tests/constants'
import { GTM_AUTH_SOURCE } from '@hozana/tracking/constants'
import { GTM } from '@hozana/tracking/gtm'
import { useTrackOnlyOnce } from '@hozana/tracking/useTrackOnlyOnce'

import { Button } from 'elements/button/Button'
import { Captcha } from 'elements/input/Captcha'
import { Form } from 'elements/input/Form'
import { FormRootError } from 'elements/input/FormRootError'
import { Input } from 'elements/input/Input'
import { InputGroup } from 'elements/input/InputGroup'
import { PasswordInput } from 'elements/input/PasswordInput'
import { Div } from 'elements/layout/Div'
import { ScrollDiv } from 'elements/layout/ScrollDiv'
import { Space } from 'elements/layout/Space'
import { Transition } from 'elements/motion/Transition'
import { P } from 'elements/text/P'
import { Alert, TAlertProps } from 'elements/ui/Alert'
import { Logo } from 'elements/ui/Logo'

import { type TConnectDetails, updateConnectDetails } from 'general/functions'

import { CONNECT_METHOD, SOCIAL_NAME } from 'modules/auth/constants'
import { getLoginMethods, logEmail, loginFormSubmit, signupFormSubmit } from 'modules/auth/routines'

import { Cgu } from './Cgu'
import { ConnectButton } from './ConnectButton'
import { DefinePasswordSection } from './DefinePasswordSection'
import { LostPwdSection, type TLostPwdSectionProps } from './LostPwdSection'
import { PreviousConnectStepButton } from './PreviousConnectStepButton'
import { SocialLoginButton } from './SocialLoginButton'
import { SocialLoginNative } from './SocialLoginNative'
import { StepTitle } from './StepTitle'
import {
  CONNECT_INPUT_ID,
  CONNECT_PADDING,
  CONNECT_STEP_CLASS,
  CONNECT_STEP_ORDER,
  STEP_INPUT_IDS,
  type TConnectStep,
} from './constants'
import { focusInput, trimData } from './functions'

const SliderDiv = styled(Div)<{ currentStep: TConnectStep }>`
  transition: transform 300ms ease;
  transform: translateX(
    -${({ currentStep }) => {
        return 34.3 * (Object.keys(STEP_INPUT_IDS).findIndex((elt) => elt === currentStep) ?? 0)
      }}%
  );
`

type TApiFormattedError = { errors?: { _error?: string } }

const isApiFormattedError = (unknownError: any): unknownError is TApiFormattedError =>
  typeof unknownError === 'object' && 'errors' in unknownError && '_error' in unknownError.errors

const getErrorMessage = (error: any) =>
  typeof error === 'object' ? (error as { errors?: { _error?: string } })?.errors?._error : undefined

/**
 * Depending on the auth source, the user can have a feeling of being logging in or signing up:
 * this function returns the tip to display for users thinking they are in the wrong place.
 */
const getTip = (authSource: GTM_AUTH_SOURCE) => {
  switch (authSource) {
    case GTM_AUTH_SOURCE.HEADER_SIGNUP:
    case GTM_AUTH_SOURCE.NAVIGATION_SIGNUP:
    case GTM_AUTH_SOURCE.HOME_PAGE:
      return 'trans:auth:auth.recover.login.already-signin'

    case GTM_AUTH_SOURCE.HEADER_LOGIN:
    case GTM_AUTH_SOURCE.NAVIGATION_LOGIN:
      return 'trans:auth:auth.recover.signup.no-account'

    default:
      return undefined
  }
}

export type TConnectFormData = {
  email: string
  password: string
  name?: string
  captcha?: string
}

export type TConnectFormProps = {
  authSource: GTM_AUTH_SOURCE
  initialTitle?: string | ReactNode
  communityId?: number
  connectDetails?: TConnectDetails
  /** ⚠️ This onSuccess callback will only run if the page does not reload on success */
  onSuccess?: VoidFunction
  onFirstChapterToggle?: (isFirstChapterActive: boolean) => void
  requestedEmail?: string
  firstButtonText?: string
  lastButtonText?: string
  solidPreviousButton?: boolean
} & Pick<TLostPwdSectionProps, 'popinOptions'>

export const ConnectForm: TFCC<TConnectFormProps> = ({
  initialTitle = 'auth:words.what-is-your-email',
  authSource,
  children = <Logo full color="darkBlue" fill="yellow" size="200px" />,
  communityId,
  connectDetails,
  solidPreviousButton,
  requestedEmail,
  firstButtonText = 'auth:words.i-continue',
  lastButtonText,
  onSuccess,
  onFirstChapterToggle,
}) => {
  const isNative = useIsNative()
  const dispatch = useDispatch()
  const {
    i18n: { language },
  } = useTranslation()
  const screen = useScreen()
  const containerRef = useRef<HTMLDivElement>(null)
  const isHydrated = useIsHydrated()
  const trackOnlyOnce = useTrackOnlyOnce()
  const {
    query: { authSource: queryAuthSource },
  } = useRouter()

  const [emailAlert, setEmailAlert] = useState<{ displayed: boolean; status?: TAlertProps['status']; text?: string }>({
    displayed: false,
  })
  const [currentStep, setCurrentStep] = useState<TConnectStep>('email')
  const [currentEmail, setCurrentEmail] = useState<string>()
  const [
    /**
     * loginMethods is:
     * - `undefined` as long as we don't set it with the api response to getLoginMethods
     * - `null` if the user does not exist
     * - `[]` if the user exists but does not have neither password nor social auth provider
     * - otherwise a subset of `['password', 'facebook', 'google', 'apple']`
     */
    loginMethods,
    setLoginMethods,
  ] = useState<CONNECT_METHOD[]>()
  const alreadySignedUp = !!loginMethods

  // shouldFocusError: false, otherwise the input is not correctly scrolled into view
  const form = useForm<TConnectFormData>({ shouldFocusError: false, defaultValues: { email: requestedEmail } })
  const {
    clearErrors,
    formState,
    handleSubmit,
    getFieldState,
    getValues,
    setError,
    setValue,
    trigger,
    unregister,
    watch,
  } = form
  const data = watch()
  const { name, email, password, captcha } = data

  useEffect(() => {
    if (!queryAuthSource) {
      trackOnlyOnce(GTM.EVENTS.CONNECT_FORM_DISPLAYED, { authSource })
    }
  }, [authSource, queryAuthSource, trackOnlyOnce])

  useEffect(() => onFirstChapterToggle?.(currentStep === 'email'), [currentStep, onFirstChapterToggle])

  useEffect(() => focusInput(CONNECT_INPUT_ID.EMAIL), [])

  useEffect(() => {
    if (alreadySignedUp) {
      setValue('name', undefined)
      unregister('name')
      unregister('captcha')
      focusInput(CONNECT_INPUT_ID.PASSWORD)
    }
  }, [alreadySignedUp, setValue, unregister])

  const getFieldInvalid = useCallback(
    (name: keyof TConnectFormData) => getFieldState(name, formState).invalid,
    [formState, getFieldState],
  )

  const onChange: ChangeEventHandler<HTMLInputElement> = () => clearErrors()

  const onEmailChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    onChange(e)
    setEmailAlert((prevEmailAlert) => ({ ...prevEmailAlert, displayed: false }))
    setLoginMethods(undefined)
  }

  const validate = useCallback(
    async (prevStep: TConnectStep, nextStep: TConnectStep) => {
      const lastStep: TConnectStep = alreadySignedUp ? 'password' : 'name'

      // Do not validate when going back
      if (CONNECT_STEP_ORDER.indexOf(nextStep) <= CONNECT_STEP_ORDER.indexOf(prevStep) && nextStep !== lastStep) {
        return true
      }

      return await trigger(prevStep, { shouldFocus: false })
    },
    [alreadySignedUp, trigger],
  )

  /**
   * - Validate current step
   * - Dispatch routines
   * - Track events
   * - Focus input
   */
  const goToStep = useCallback(
    async (nextStep: TConnectStep) => {
      if (nextStep !== currentStep && (await validate(currentStep, nextStep))) {
        setCurrentStep(nextStep)
        focusInput(STEP_INPUT_IDS[nextStep], screen.xs || screen.sm, containerRef.current)

        if (currentStep === 'email' && currentEmail !== email) {
          setCurrentEmail(email)
          dispatch(routineToThunk(getLoginMethods, { email })).then((newLoginMethods) => {
            setLoginMethods(newLoginMethods)
            if (newLoginMethods) {
              trackOnlyOnce(GTM.EVENTS.LOGIN_METHOD_FOUND)
            } else {
              trackOnlyOnce(GTM.EVENTS.LEAD)
              dispatch(routineToThunk(logEmail, { email, communityId }))
            }
          })
        }
      }
    },
    [currentStep, email, validate, screen.xs, screen.sm, currentEmail, dispatch, communityId, trackOnlyOnce],
  )

  const onBeforeSubmit = useCallback(
    (method: CONNECT_METHOD) => {
      updateConnectDetails({
        ...connectDetails,
        gtmData: [
          ...(connectDetails?.gtmData || []),
          {
            event: alreadySignedUp ? GTM.EVENTS.LOGIN_FORM_SUBMIT_SUCCESS : GTM.EVENTS.SIGNUP_FORM_SUBMIT_SUCCESS,
            method,
          },
        ],
      })
      trackOnlyOnce(alreadySignedUp ? GTM.EVENTS.LOGIN_FORM_SUBMITTED : GTM.EVENTS.SIGNUP_FORM_SUBMITTED, { method })
    },
    [alreadySignedUp, connectDetails, trackOnlyOnce],
  )

  const onConnectPasswordError = useCallback(
    (invalidStep: TConnectStep, error: string) => {
      GTM.trackEvent(GTM.EVENTS.SIGNUP_FORM_SUBMIT_FAILURE, { method: CONNECT_METHOD.PASSWORD, invalidStep, error })
      if (invalidStep) {
        goToStep(invalidStep)
      }
    },
    [goToStep],
  )

  const createOnSocialError = useCallback(
    (method: CONNECT_METHOD) => (error: unknown) => {
      trackOnlyOnce(alreadySignedUp ? GTM.EVENTS.LOGIN_FORM_SUBMIT_FAILURE : GTM.EVENTS.SIGNUP_FORM_SUBMIT_FAILURE, {
        method,
        error: getErrorMessage(error),
      })
    },
    [alreadySignedUp, trackOnlyOnce],
  )

  /**
   * Executes on submit: on hit "Enter" and on click "Continue" or "Suscribe" (submit) buttons
   * Really submit the form only when the current step is the last,
   * otherwise go to the next step.
   */
  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      const isLastStep = alreadySignedUp ? currentStep === 'password' : currentStep === 'name'

      if (isLastStep) {
        onBeforeSubmit(CONNECT_METHOD.PASSWORD)
        handleSubmit(
          (data) =>
            bindRoutineToHookForm(alreadySignedUp ? loginFormSubmit : signupFormSubmit, {
              dispatch,
              setError,
              onFailure: (error: any) => {
                const message = isApiFormattedError(error) ? error.errors._error : undefined
                const invalidStep = message
                  ? CONNECT_STEP_ORDER.find((connectStep) => message.includes(connectStep))
                  : undefined
                setError(invalidStep ?? 'root', { message })
                console.log('From ConnectForm, handleSubmit onFailure.', { error, message, invalidStep, onSuccess })
                onConnectPasswordError(invalidStep, message)
              },
              onSuccess,
            })(trimData(data)),
          (hookFormFieldErrors) => {
            const invalidStep = CONNECT_STEP_ORDER.find(
              (step) => Object.keys(hookFormFieldErrors).includes(step) && hookFormFieldErrors[step],
            )
            console.log('From ConnectForm, handleSubmit onInvalid.', { hookFormFieldErrors, invalidStep })
            onConnectPasswordError(
              invalidStep,
              invalidStep ? hookFormFieldErrors[invalidStep].message : hookFormFieldErrors.root?.message,
            )
          },
        )(e)
      } else {
        // Prevent the global submit
        e.preventDefault()
        if (currentStep === 'email') {
          trackOnlyOnce(GTM.EVENTS.CONNECT_FORM_EMAIL_SUBMITTED)
        } else if (!alreadySignedUp && currentStep === 'password') {
          trackOnlyOnce(GTM.EVENTS.SIGNUP_FORM_PASSWORD_SUBMITTED)
        }
        const nextStep: TConnectStep = currentStep === 'email' || alreadySignedUp ? 'password' : 'name'
        validate(currentStep, nextStep)
          .then((isValid) => {
            if (isValid) {
              goToStep(nextStep)
            }
          })
          .catch()
      }
    },
    [
      alreadySignedUp,
      currentStep,
      dispatch,
      goToStep,
      handleSubmit,
      onBeforeSubmit,
      onConnectPasswordError,
      onSuccess,
      setError,
      trackOnlyOnce,
      validate,
    ],
  )

  const tip = getTip(authSource)

  const formRoorError = <FormRootError m="10px 0 0" form={form} />

  return (
    <Form
      id="ConnectForm"
      form={form}
      method="POST"
      action={isHydrated ? undefined : '/api/front/login'} // No js fallback
      onSubmit={onSubmit}
      noValidate
      h="100%"
      p="0 0 10px"
    >
      <Div align="center">
        <PreviousConnectStepButton {...{ solidPreviousButton, currentStep, goToStep }} />
        {children}
      </Div>
      <ScrollDiv
        overflowX="hidden"
        w="100%"
        scrollbarWidth="none"
        ref={containerRef}
        h="unset"
        p={`0 ${CONNECT_PADDING.EXT}px ${CONNECT_PADDING.TOTAL}px`}
      >
        {/* 330% - 3 * 30.3% + topAround = gaps around chapters */}
        <SliderDiv currentStep={currentStep} w="330%" align="topBetween" p={`0 ${CONNECT_PADDING.TOTAL}px 0 0`}>
          {/* EMAIL CHAPTER */}
          <Div w="30.3%" p="0 10px" className={CONNECT_STEP_CLASS} align="center">
            <StepTitle>{initialTitle}</StepTitle>
            <Transition initial={{ height: '0px' }} final={{ height: '250px' }} active={emailAlert.displayed}>
              {
                <Alert status={emailAlert.status} hideCloseButton m="0 0 1em">
                  <P fontSize="1em">{emailAlert.text}</P>
                </Alert>
              }
            </Transition>
            <InputGroup name="email" options={[required(), checkEmail(), { onChange: onEmailChange }]}>
              <Input
                id={CONNECT_INPUT_ID.EMAIL}
                type="email"
                tabIndex={currentStep === 'email' ? 1 : -1}
                placeholder="trans:auth:words.your-email-address"
                autoFocus={!screen.isMobile}
                fontSize="19px"
                data-testid={QA.AUTH.EMAIL.INPUT}
              />
            </InputGroup>
            {formRoorError}
            <ConnectButton
              /* Disable button only if an following step is filled so that the button is not disable at first */
              disabled={!!((!email || getFieldInvalid('email')) && (password || name))}
              tabIndex={currentStep === 'email' ? 1 : -1}
              data-testid={QA.AUTH.EMAIL.SUBMIT_BUTTON}
            >
              {firstButtonText}
            </ConnectButton>

            {!communityId && (
              /* Display Social Login Buttons if we are not in the LandingCommunityPage */
              <Transition initial={{ height: '0px' }} final={{ height: '250px' }} active={currentStep === 'email'}>
                <Space />
                {!isNative ? (
                  <>
                    {/** To avoid UI issue, we have to wrap each social login button by a Div */}
                    <Div>
                      <SocialLoginButton
                        onClick={() => onBeforeSubmit(CONNECT_METHOD.FACEBOOK)}
                        onSuccess={onSuccess}
                        onError={createOnSocialError(CONNECT_METHOD.FACEBOOK)}
                        socialName={SOCIAL_NAME.FACEBOOK}
                      />
                    </Div>
                    <Div>
                      <SocialLoginButton
                        onClick={() => onBeforeSubmit(CONNECT_METHOD.GOOGLE)}
                        onSuccess={onSuccess}
                        onError={createOnSocialError(CONNECT_METHOD.GOOGLE)}
                        socialName={SOCIAL_NAME.GOOGLE}
                      />
                    </Div>
                  </>
                ) : (
                  <SocialLoginNative onClick={() => onBeforeSubmit(CONNECT_METHOD.NATIVE)} m="10px 0" />
                )}
              </Transition>
            )}
          </Div>

          {/* PASSWORD/SSO CHAPTER */}

          <Div w="30.3%" p="0 10px" className={CONNECT_STEP_CLASS} align="center">
            <StepTitle>
              <P bold>
                {alreadySignedUp
                  ? loginMethods?.length === 0
                    ? 'trans:auth:auth.login.nearly-there'
                    : 'trans:auth:auth.login.welcome'
                  : 'trans:auth:auth.signup.choose-password'}
              </P>
            </StepTitle>
            <Transition
              initial={{ height: '0px' }}
              final={{ height: '100px' }}
              active={currentStep === 'password' && loginMethods?.includes(CONNECT_METHOD.PASSWORD)}
            >
              <P fontSize="1em" bold m="0 0 1em">
                trans:auth:words.password.enter
              </P>
            </Transition>
            <Transition
              initial={{ height: '0px' }}
              final={{ height: '200px' }}
              renderWhenInvisible
              active={!alreadySignedUp || loginMethods?.includes(CONNECT_METHOD.PASSWORD) || currentStep !== 'password'}
            >
              {(!isHydrated || currentStep !== 'email') && (
                <InputGroup
                  name="password"
                  options={[
                    required(),
                    customCondition(
                      (value) => value !== getValues('name'),
                      'trans:common:common.form.error.password-is-name',
                    ),
                    { onChange },
                  ]}
                >
                  <PasswordInput
                    id={CONNECT_INPUT_ID.PASSWORD}
                    tabIndex={!isHydrated ? 2 : currentStep === 'password' ? undefined : -1}
                    placeholder="auth:words.password.placeholder"
                    fontSize="1.4em"
                    required={false}
                    data-testid={QA.AUTH.PASSWORD.INPUT}
                  />
                </InputGroup>
              )}
              {formRoorError}
              <ConnectButton
                /* Disable button only if the following step is filled so that the button is not disable at first */
                disabled={!!((!password || getFieldInvalid('password')) && name)}
                tabIndex={currentStep === 'password' ? undefined : -1}
                data-testid={QA.AUTH.PASSWORD.SUBMIT_BUTTON}
              >
                {alreadySignedUp ? lastButtonText ?? 'trans:auth:auth.login.i-connect' : 'trans:auth:words.i-continue'}
              </ConnectButton>
            </Transition>

            <Transition
              initial={{ height: '0px' }}
              final={{ height: '300px' }}
              active={alreadySignedUp && currentStep === 'password'}
            >
              {loginMethods?.includes(CONNECT_METHOD.PASSWORD) ? (
                <LostPwdSection email={email} />
              ) : (
                <>
                  {!isNative && loginMethods?.includes(CONNECT_METHOD.FACEBOOK) && (
                    <Div>
                      {/** To avoid UI issue, we have to wrap each social login button by a Div */}
                      <SocialLoginButton
                        onClick={() => onBeforeSubmit(CONNECT_METHOD.FACEBOOK)}
                        onSuccess={onSuccess}
                        onError={createOnSocialError(CONNECT_METHOD.FACEBOOK)}
                        socialName={SOCIAL_NAME.FACEBOOK}
                      />
                    </Div>
                  )}
                  <Div>
                    {!isNative && loginMethods?.includes(CONNECT_METHOD.GOOGLE) && (
                      <SocialLoginButton
                        onClick={() => onBeforeSubmit(CONNECT_METHOD.GOOGLE)}
                        onSuccess={onSuccess}
                        onError={createOnSocialError(CONNECT_METHOD.GOOGLE)}
                        socialName={SOCIAL_NAME.GOOGLE}
                      />
                    )}
                  </Div>
                </>
              )}

              {isNative && (
                <>
                  <P bold fontSize="1em">
                    trans:auth:auth.social.appleid.only
                  </P>
                  {isNative && <SocialLoginNative onClick={() => onBeforeSubmit(CONNECT_METHOD.NATIVE)} m="10px 0" />}
                </>
              )}
              {alreadySignedUp && (
                <DefinePasswordSection
                  show={
                    alreadySignedUp && currentStep === 'password' && !loginMethods?.includes(CONNECT_METHOD.PASSWORD)
                  }
                  email={email}
                  solidButton={loginMethods?.length === 0}
                >
                  {loginMethods?.includes(CONNECT_METHOD.NATIVE) && loginMethods.length === 1 && !isNative
                    ? 'trans:auth:auth.social.appleid.define-password'
                    : loginMethods?.length === 0
                    ? 'trans:auth:auth.login.new-password-required'
                    : 'trans:auth:auth.login.define-password'}
                </DefinePasswordSection>
              )}
            </Transition>
          </Div>

          {/* NAME+CAPTCHA CHAPTER */}

          <Div w="30.3%" p="0 10px" className={CONNECT_STEP_CLASS} align="center">
            {!alreadySignedUp && (
              <>
                <StepTitle>
                  <P bold>trans:auth:words.what-is-your-firstname</P>
                </StepTitle>
                <InputGroup name="name" options={[required(), { onChange }]}>
                  <Input
                    id={CONNECT_INPUT_ID.NAME}
                    placeholder="trans:auth:word.your-firstname"
                    tabIndex={currentStep === 'name' ? undefined : -1}
                    fontSize="1.4em"
                    onBlur={() => {
                      if (name) {
                        trackOnlyOnce(GTM.EVENTS.SIGNUP_FORM_NAME_FILLED)
                      }
                    }}
                    data-testid={QA.AUTH.NAME.INPUT}
                  />
                </InputGroup>

                {/* Hide Captcha as long as this chapter is not visible so that the form height is not too big */}
                <Transition initial={{ height: '0px' }} final={{ height: '85px' }} active={currentStep === 'name'}>
                  <Space h="10px" />
                  <Captcha
                    value={captcha}
                    onChange={(token) => {
                      if (token) {
                        clearErrors()
                        setValue('captcha', token)
                        trackOnlyOnce(GTM.EVENTS.SIGNUP_FORM_CAPTCHA_VALID)
                      }
                    }}
                    tabIndex={!isHydrated || currentStep === 'name' ? undefined : -1}
                    scale={0.96}
                    lang={language}
                  />
                </Transition>
                {formRoorError}
                <ConnectButton
                  tabIndex={currentStep === 'name' ? undefined : -1}
                  data-testid={QA.AUTH.NAME.SUBMIT_BUTTON}
                  disabled={formState.isSubmitting}
                >
                  {lastButtonText ??
                    (formState.isSubmitting ? 'trans:auth:auth.signup.ongoing' : 'trans:auth:words.i-subscribe')}
                </ConnectButton>
              </>
            )}
          </Div>
        </SliderDiv>
      </ScrollDiv>

      <Div p={`0 ${CONNECT_PADDING.TOTAL}px ${CONNECT_PADDING.TOTAL}px`}>
        {!alreadySignedUp && (
          <Cgu
            text={currentStep === 'name' ? 'trans:auth:auth.sign.terms.accept' : 'trans:auth:auth.sign.terms.free'}
          />
        )}
        {tip && (
          <Transition
            initial={{ height: '0px' }}
            final={{ height: '100px' }}
            active={currentStep === 'email' && !emailAlert.displayed}
          >
            <Space />
            <Button
              type="button"
              colors="greyTransparent"
              color="blue"
              hoverColor="blue"
              size="big"
              tabIndex={tip ? undefined : -1}
              onClick={() =>
                setEmailAlert({ displayed: true, status: 'info', text: 'trans:auth:auth.recover.signup.fill-email' })
              }
              bold={false}
              w="100%"
            >
              {tip}
            </Button>
          </Transition>
        )}
        <Transition
          initial={{ height: '0px' }}
          final={{ height: '100px' }}
          active={currentStep !== 'email' && loginMethods === null}
        >
          <Space />
          <Button
            type="button"
            colors="greyTransparent"
            color="blue"
            hoverColor="blue"
            size="big"
            tabIndex={tip ? undefined : -1}
            onClick={() => {
              goToStep('email')
              setEmailAlert({ displayed: true, status: 'error', text: 'trans:auth:auth.login.check-email' })
            }}
            bold={false}
            w="100%"
          >
            <Trans i18nKey="auth:auth.signup.already-have-an-account" components={{ u: <u /> }} />
          </Button>
        </Transition>
      </Div>
    </Form>
  )
}
